/**
 * Calendar Tab - Displays supplier-specific calendar events.
 * Shows upcoming events, key due dates, and supplier activities filtered to the selected supplier.
 * Reuses the calendar module components with supplier-specific filtering.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Views } from 'react-big-calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Loader2,
  Calendar as CalendarIcon,
  List,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { calendarAPI, fetchAllPages, type CalendarEvent, type SupplierProfile } from '../../lib/api';
import {
  CalendarView,
  ListView,
  EVENT_TYPE_COLORS,
  EVENT_TYPE_LABELS,
  ITEMS_PER_PAGE,
  AUDIT_ACTION_PREFIX,
  auditActionPath,
  transformEventsForCalendar,
  groupEventsByMonth,
  type CalendarViewType,
  type ViewMode,
} from '../calendar-module';

interface CalendarTabProps {
  supplier: SupplierProfile;
  onTabChange?: (tab: string) => void;
}

export function CalendarTab({ supplier, onTabChange }: CalendarTabProps) {
  const navigate = useNavigate();

  // View states
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [calendarView, setCalendarView] = useState<CalendarViewType>(Views.WEEK as CalendarViewType);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Data states
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination for list view
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch supplier-specific events
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Walk pages — the backend clamps page_size, so one request capped
      // the supplier's calendar history at one page.
      const allEvents = await fetchAllPages<CalendarEvent>(
        (page) => calendarAPI.getEvents({
          supplier: supplier.company,
          ordering: 'start_datetime',
          page_size: 100,
          page,
          include_synthetic: 'true',
        }),
      );
      setEvents(allEvents);
    } catch (err: unknown) {
      console.error('Failed to fetch calendar events:', err);
      setError('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  }, [supplier.company]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Handle event click - navigate to related section
  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      const id = String(event.id ?? '');
      if (id.startsWith('approval:')) {
        // Switch to Approvals tab within supplier detail page
        if (onTabChange) {
          onTabChange('approvals');
        }
        return;
      }
      if (id.startsWith('contract:')) {
        // Switch to Contracts tab within supplier detail page
        if (onTabChange) {
          onTabChange('contracts');
        }
        return;
      }
      if (id.startsWith(AUDIT_ACTION_PREFIX)) {
        // Audit Lite: open the action, highlighted, under Questionnaires › Audits › Actions.
        navigate(auditActionPath(id));
        return;
      }
      if (id.startsWith('qbr:')) {
        // QBR events - could open a modal or navigate to a QBR section
        // For now, stay on calendar
        return;
      }
      // NCR-related events - navigate to Quality page
      if (id.startsWith('ncr_ca:') || id.startsWith('ncr_car:') || id.startsWith('action:')) {
        navigate('/quality');
        return;
      }
      // For other events, could open a detail modal
    },
    [navigate, onTabChange]
  );

  // Calendar navigation
  const handleNavigate = useCallback((newDate: Date) => {
    setCurrentDate(newDate);
  }, []);

  const handleNavigateToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleNavigatePrevious = useCallback(() => {
    const newDate = new Date(currentDate);
    if (calendarView === Views.MONTH) {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (calendarView === Views.WEEK) {
      newDate.setDate(newDate.getDate() - 7);
    } else if (calendarView === Views.DAY) {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  }, [currentDate, calendarView]);

  const handleNavigateNext = useCallback(() => {
    const newDate = new Date(currentDate);
    if (calendarView === Views.MONTH) {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (calendarView === Views.WEEK) {
      newDate.setDate(newDate.getDate() + 7);
    } else if (calendarView === Views.DAY) {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  }, [currentDate, calendarView]);

  // Transform events for calendar
  const calendarEvents = useMemo(
    () => transformEventsForCalendar(events),
    [events]
  );

  // Sorted events for list view
  const sortedEvents = useMemo(() => {
    return [...events].sort(
      (a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
    );
  }, [events]);

  // Pagination
  const totalPages = Math.ceil(sortedEvents.length / ITEMS_PER_PAGE);
  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedEvents.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedEvents, currentPage]);

  const paginatedEventsByMonth = useMemo(
    () => groupEventsByMonth(paginatedEvents),
    [paginatedEvents]
  );

  // Get upcoming events count and overdue count
  const { upcomingCount, overdueCount } = useMemo(() => {
    const now = new Date();
    const upcoming = events.filter(e => new Date(e.start_datetime) > now).length;
    const overdue = events.filter(e => {
      const endDate = new Date(e.end_datetime);
      return endDate < now && e.status !== 'completed' && e.status !== 'cancelled';
    }).length;
    return { upcomingCount: upcoming, overdueCount: overdue };
  }, [events]);

  // Format date range for header
  const getDateRangeLabel = () => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    if (calendarView === Views.MONTH) {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (calendarView === Views.WEEK) {
      const weekStart = new Date(currentDate);
      weekStart.setDate(currentDate.getDate() - currentDate.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return `${weekStart.toLocaleDateString('en-US', options)} - ${weekEnd.toLocaleDateString('en-US', options)}`;
    } else {
      return currentDate.toLocaleDateString('en-US', options);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Supplier Calendar
            {!loading && (
              <Badge variant="outline" className="ml-2">
                {events.length} {events.length === 1 ? 'event' : 'events'}
              </Badge>
            )}
          </CardTitle>

          {/* Summary badges */}
          {!loading && events.length > 0 && (
            <div className="flex gap-2">
              {overdueCount > 0 && (
                <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {overdueCount} Overdue
                </Badge>
              )}
              {upcomingCount > 0 && (
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                  <Clock className="h-3 w-3 mr-1" />
                  {upcomingCount} Upcoming
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">
            <p>{error}</p>
            <Button onClick={fetchEvents} className="mt-4">
              Retry
            </Button>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No calendar events found for this supplier.</p>
            <p className="text-sm mt-2">Events will appear here when approvals, contracts, or meetings are scheduled.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex items-center justify-between flex-wrap gap-4 pb-4 border-b">
              {/* Navigation */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleNavigatePrevious}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleNavigateNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleNavigateToday}>
                  Today
                </Button>
                <span className="text-sm font-medium ml-2">{getDateRangeLabel()}</span>
              </div>

              {/* View Controls */}
              <div className="flex items-center gap-2">
                {/* Calendar/List toggle */}
                <div className="flex border rounded-lg overflow-hidden">
                  <Button
                    variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-none"
                    onClick={() => setViewMode('calendar')}
                  >
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    Calendar
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-none"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4 mr-1" />
                    List
                  </Button>
                </div>

                {/* View type selector (only for calendar mode) */}
                {viewMode === 'calendar' && (
                  <div className="flex border rounded-lg overflow-hidden">
                    <Button
                      variant={calendarView === Views.DAY ? 'default' : 'ghost'}
                      size="sm"
                      className="rounded-none"
                      onClick={() => setCalendarView(Views.DAY as CalendarViewType)}
                    >
                      Day
                    </Button>
                    <Button
                      variant={calendarView === Views.WEEK ? 'default' : 'ghost'}
                      size="sm"
                      className="rounded-none"
                      onClick={() => setCalendarView(Views.WEEK as CalendarViewType)}
                    >
                      Week
                    </Button>
                    <Button
                      variant={calendarView === Views.MONTH ? 'default' : 'ghost'}
                      size="sm"
                      className="rounded-none"
                      onClick={() => setCalendarView(Views.MONTH as CalendarViewType)}
                    >
                      Month
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Event Type Legend */}
            <div className="flex flex-wrap gap-3 py-2">
              {Object.entries(EVENT_TYPE_LABELS).map(([type, label]) => {
                const hasEvents = events.some(e => e.event_type === type);
                if (!hasEvents) return null;
                return (
                  <div key={type} className="flex items-center gap-1.5 text-xs">
                    <div className={`w-3 h-3 rounded-full ${EVENT_TYPE_COLORS[type]}`} />
                    <span className="text-muted-foreground">{label}</span>
                  </div>
                );
              })}
            </div>

            {/* Calendar or List View */}
            {viewMode === 'calendar' ? (
              <CalendarView
                events={calendarEvents}
                view={calendarView}
                date={currentDate}
                onEventClick={handleEventClick}
                onSelectSlot={() => {}}
                onNavigate={handleNavigate}
              />
            ) : (
              <ListView
                allListEvents={sortedEvents}
                paginatedEventsByMonth={paginatedEventsByMonth}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onEventClick={handleEventClick}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
